/**
 * DigX Reviews -- the photo lightbox. 2026-08-28.
 *
 * WHAT IT REPLACES. Every review photo was a bare <a href> to the raw image
 * file, so clicking one navigated away from the product page with no way
 * back but the browser's Back button. Nikita: "Make sure you can open and
 * close photos." This file intercepts that click and opens the photo over
 * the page instead.
 *
 * IT IS AN ENHANCEMENT, NEVER A REQUIREMENT. The trigger is still a real
 * <a href> pointing at the full image, so with JS disabled -- or with the
 * shop owner's "open photos in an overlay" setting off, in which case
 * sections/digx-reviews.liquid ships neither this file nor the overlay
 * shell -- clicking a photo still opens that photo. There is no state in
 * which a shopper meets a control that does nothing. This is also why the
 * trigger is an anchor and not a <button>: a button with no script behind
 * it is exactly that dead control.
 *
 * PORTED FROM lib/reviews/fragments.ts (renderLightbox / LIGHTBOX_SCRIPT).
 * Same class names, same open/close/prev/next contract, same focus trap and
 * the same `window.__digxLightboxInit` double-bind guard. The differences
 * are the ones this surface forces:
 *
 *   - The app opens the overlay from a WHOLE review card and reads its
 *     media out of a hidden <template>. Here the trigger is one PHOTO, and
 *     the media list is read straight off the sibling anchors: each item's
 *     full image is its own href and its thumb is its inner img. No
 *     template carrier, no extra nodes, nothing fetched.
 *   - The app binds Enter and Space itself because its trigger is a <div>.
 *     Ours is an anchor, so the browser already fires a click on Enter and
 *     the delegated click handler below catches it. Binding keys again
 *     would fire twice.
 *   - No video. The inline <video controls> in the review row stays exactly
 *     where it is: it already plays with JS disabled, and moving it in here
 *     would make a working control depend on this file.
 *   - The dialog's SHAPE comes from the photo. The app letterboxes inside a
 *     fixed stage; here the photo's natural width, height and ratio are
 *     published as CSS custom properties and the stylesheet sizes the
 *     dialog from them, so the picture fills its panel with no bands and
 *     still never renders larger than its source. See publishNaturalSize().
 *
 * DELEGATED FROM `document`, WHICH IS THE WHOLE POINT. Reviews are appended
 * to the list by assets/digx-reviews.js's load-more, straight from a server
 * endpoint. A per-anchor listener would have to be rebound after every
 * append and would silently miss any path that forgot to. Delegation makes
 * a photo on page 4 work with zero rebinding.
 *
 * NOT ONE WORD OF USER-VISIBLE COPY LIVES IN THIS FILE. The button labels
 * are rendered by Liquid from the locale files; the only string this script
 * builds is the "2 de 5" counter, and it does that by substituting two
 * NUMBERS into a template the snippet passes in via data-counter-template.
 */
(function () {
  "use strict";

  // Same idiom as the app's own version: one flag, so a second copy of this
  // file on the same page cannot double-bind the delegated handlers.
  if (window.__digxLightboxInit) return;
  window.__digxLightboxInit = true;

  var lightbox = document.getElementById("digx-lightbox");
  // No shell means the shop owner turned the overlay off (or this file was
  // loaded on a page with no reviews widget). Return before binding
  // anything at all: the photo anchors then behave as the plain links they
  // have always been.
  if (!lightbox) return;

  var widget = document.querySelector("[data-digx-reviews]");
  if (widget && widget.getAttribute("data-lightbox") === "false") return;

  var dialog = lightbox.querySelector(".digx-lightbox__dialog");
  var stageImg = lightbox.querySelector(".digx-lightbox__image");
  var prevBtn = lightbox.querySelector(".digx-lightbox__nav--prev");
  var nextBtn = lightbox.querySelector(".digx-lightbox__nav--next");
  var counterEl = lightbox.querySelector("[data-digx-lightbox-counter]");
  var authorEl = lightbox.querySelector(".digx-lightbox__author");
  var verifiedEl = lightbox.querySelector(".digx-lightbox__verified");
  var starsEl = lightbox.querySelector(".digx-lightbox__stars");
  var dateEl = lightbox.querySelector(".digx-lightbox__date");
  var titleEl = lightbox.querySelector(".digx-lightbox__title");
  var bodyEl = lightbox.querySelector(".digx-lightbox__body");
  // Empty when show_votes is off: the Liquid snippet gates the whole block,
  // so this is just [] on a store that switched the useful row off, and
  // every use below is already guarded on .length.
  var voteButtons = Array.prototype.slice.call(lightbox.querySelectorAll(".digx-lightbox__vote-btn"));
  if (!dialog || !stageImg) return;

  // "DIGXCURRENT de DIGXTOTAL" -- shop-owned copy, two literal placeholders
  // the snippet passed through the `t` filter. This script only ever swaps
  // numbers into it.
  var counterTemplate = lightbox.getAttribute("data-counter-template") || "";

  var state = {
    items: [],
    index: 0,
    opener: null,
    scrollY: 0,
    bodyTop: "",
    bodyPadRight: "",
    // Whether the page is currently pinned. Without this flag, opening a
    // second photo while the lightbox is already open runs lockScroll()
    // again, and the second run reads pageYOffset as 0 (the body is already
    // fixed), overwriting the real origin. Closing then threw the shopper to
    // the very top of the page and left a negative `top` on the body.
    // Measured 2026-08-28: 2821 -> 0.
    locked: false
  };

  /**
   * Every photo of ONE review, in DOM order. Scoped to the clicked photo's
   * own .digx-review__card so a shopper paging through review 3's photos
   * can never walk into review 4's.
   */
  function collectFrom(card) {
    return Array.prototype.slice
      .call(card.querySelectorAll("[data-digx-lightbox-open]"))
      .map(function (anchor) {
        var img = anchor.querySelector("img");
        return {
          full: anchor.getAttribute("href"),
          thumb: img ? img.getAttribute("src") : "",
          anchor: anchor
        };
      })
      .filter(function (item) {
        return !!item.full;
      });
  }

  /**
   * Author, verified pill, stars and date, copied off the card that is
   * already on the page. Nothing is fetched and nothing is invented: if the
   * card does not carry a field, the overlay does not show one.
   *
   * textContent throughout, never innerHTML. The star row only ever holds
   * the two fixed glyphs and the author was escaped server-side, but
   * copying as text keeps that provably safe without relying on either.
   */
  function fillInfo(card) {
    var author = card.querySelector(".digx-review__author");
    var verified = card.querySelector(".digx-badge--outline");
    var stars = card.querySelector(".digx-review__stars");
    var date = card.querySelector(".digx-review__date");

    if (authorEl) authorEl.textContent = author ? author.textContent : "";

    if (verifiedEl) {
      // Copied from the row rather than translated again here, so the pill
      // can never disagree with the pill six pixels behind it.
      verifiedEl.textContent = verified ? verified.textContent : "";
      verifiedEl.hidden = !verified;
    }

    if (starsEl) {
      starsEl.textContent = stars ? stars.textContent : "";
      starsEl.setAttribute("aria-label", stars ? stars.getAttribute("aria-label") || "" : "");
    }

    if (dateEl) {
      dateEl.textContent = date ? date.textContent : "";
      dateEl.setAttribute("datetime", date ? date.getAttribute("datetime") || "" : "");
    }

    // The review's own words. Copied from the row, never re-fetched, so the
    // overlay can never show text that disagrees with the card behind it.
    // Without these two the info panel held a name, five stars and a date,
    // and half of a 704px dialog rendered as empty white (measured
    // 2026-08-28 on the real store).
    var title = card.querySelector(".digx-review__title");
    if (titleEl) {
      titleEl.textContent = title ? title.textContent : "";
      // Most reviews have no separate title: for wizard submissions `title`
      // is just the first 100 characters of `body`, so the Liquid suppresses
      // it and printing it here would show the same sentence twice.
      titleEl.hidden = !title;
    }

    var body = card.querySelector(".digx-review__body");
    if (bodyEl) bodyEl.textContent = body ? body.textContent : "";

    // THE HELPFUL VOTE ROW, 2026-08-29. Same "copy from the card, never
    // re-fetch" idiom as every field above, extended to the vote buttons'
    // own STATE, not just their text -- so a review already voted on shows
    // that the instant the overlay opens, instead of always starting blank.
    //
    // data-review-id IS THE WHOLE MECHANISM. assets/digx-reviews.js's
    // wireButton() reads this attribute FRESH at click time (not once at
    // page load), and its render() re-queries every [data-digx-vote] button
    // under the widget root that shares a review id -- so the moment these
    // two overlay buttons carry the SAME id as the row's own two buttons,
    // pressing either copy updates both, with zero new mechanism. Do not
    // invent a second vote store here; this block only ever copies what the
    // row already carries.
    if (voteButtons.length) {
      var rowVoteButtons = card.querySelectorAll("[data-digx-vote]");
      voteButtons.forEach(function (btn) {
        var direction = btn.getAttribute("data-digx-vote");
        var rowBtn = null;
        Array.prototype.forEach.call(rowVoteButtons, function (rb) {
          if (rb.getAttribute("data-digx-vote") === direction) rowBtn = rb;
        });
        var reviewId = rowBtn ? rowBtn.getAttribute("data-review-id") || "" : "";
        btn.setAttribute("data-review-id", reviewId);

        var pressed = !!rowBtn && rowBtn.getAttribute("aria-pressed") === "true";
        btn.setAttribute("aria-pressed", pressed ? "true" : "false");
        btn.classList.toggle("is-active", pressed);

        var countEl = btn.querySelector("[data-digx-vote-count]");
        var rowCountEl = rowBtn ? rowBtn.querySelector("[data-digx-vote-count]") : null;
        if (countEl) {
          countEl.textContent = rowCountEl ? rowCountEl.textContent : "0";
          if (rowCountEl) {
            countEl.setAttribute("data-base-count", rowCountEl.getAttribute("data-base-count") || "0");
          }
        }
      });
    }
  }

  /**
   * THE PHOTO'S OWN MEASUREMENTS, PUBLISHED TO CSS. 2026-08-29.
   *
   * Nikita: "on image previews make sure we don't have these borders,
   * always fill the image." The borders were letterbox bands: the stage had
   * a `min-height: 320px` and the photo measured 281 tall, so ~20px of the
   * panel's own field showed above and below it.
   *
   * Filling the panel and never upscaling are only compatible if the PANEL
   * follows the PHOTO, so these three custom properties are the whole
   * mechanism. assets/digx-reviews-lightbox.css reads them to size the
   * dialog: --digx-photo-ratio drives the media panel's aspect-ratio,
   * --digx-photo-w caps that panel's WIDTH directly at the source (pass 7,
   * 2026-08-29 -- earlier this caped the whole dialog at twice the source,
   * back when the media half was always exactly half the dialog; the text
   * column is a fixed 400px now, so the cap moved to the panel it actually
   * bounds), and --digx-photo-h is one term in the dialog's max-height.
   * Because the panel's aspect-ratio is the photo's own, a width capped at
   * naturalWidth can never derive a height past naturalHeight either, so
   * `object-fit: cover` can only ever scale DOWN -- the workspace's "placed
   * pixels never exceed source pixels" rule holds by construction rather
   * than by anyone remembering to check it.
   *
   * THIS REPLACES the old inline `style.maxWidth = naturalWidth + "px"`
   * cap. That one had to go rather than be added to: an inline max-width
   * OVERRIDES the stylesheet's own `max-width: 100%`, which is why the
   * image measured 500px wide inside a 480px panel on 2026-08-28. One
   * mechanism, in one place, or they fight.
   */
  function publishNaturalSize() {
    if (!stageImg.naturalWidth || !stageImg.naturalHeight) return;
    lightbox.style.setProperty("--digx-photo-w", stageImg.naturalWidth + "px");
    lightbox.style.setProperty("--digx-photo-h", stageImg.naturalHeight + "px");
    lightbox.style.setProperty(
      "--digx-photo-ratio",
      String(stageImg.naturalWidth / stageImg.naturalHeight)
    );
  }

  function clearNaturalSize() {
    lightbox.style.removeProperty("--digx-photo-w");
    lightbox.style.removeProperty("--digx-photo-h");
    lightbox.style.removeProperty("--digx-photo-ratio");
  }

  // PASS 6's fitDialogHeight() / --digx-dialog-h LIVED HERE AND IS DELETED,
  // pass 7, 2026-08-29. It made the info panel's own scrollHeight the
  // dialog's definite height, with the media panel's width DERIVED from
  // that height through `display: grid` in the stylesheet. It satisfied
  // the empty-tail check it was built for and still shipped a defect worse
  // than the one it fixed: a short review crushed the photo to a ~180px
  // sliver beside a 780px column of near-empty text. See
  // assets/digx-reviews-lightbox.css's file header, PASS 6 and PASS 7, for
  // the full account. The stylesheet now sizes the media panel's WIDTH
  // directly (capped at the photo's own naturalWidth) and derives its
  // height from that through `aspect-ratio`, the ordinary direction that
  // resolves in an ordinary flex row -- nothing left for this file to
  // measure or publish for the dialog's shape, so the info panel's own
  // scrollHeight is no longer read anywhere and its element reference is
  // gone with it.

  function show(index) {
    var total = state.items.length;
    if (total === 0) return;

    // Wraps in both directions, so "next" on the last photo returns to the
    // first instead of dead-ending on a button that still looks pressable.
    state.index = ((index % total) + total) % total;

    stageImg.src = state.items[state.index].full;
    stageImg.hidden = false;

    // PER-PHOTO STATE IS CLEARED, NEVER CARRIED. A photo that is already in
    // the browser's cache reports its natural size synchronously, so it is
    // measured on the spot and nothing flickers. One that is not gets the
    // previous photo's numbers REMOVED first and the stylesheet's neutral
    // fallbacks until it loads -- otherwise a small photo followed by a
    // large one would leave the dialog pinned to the small one's shape,
    // which is the defect the old inline cap had.
    if (stageImg.complete && stageImg.naturalWidth) {
      publishNaturalSize();
    } else {
      clearNaturalSize();
      stageImg.addEventListener("load", publishNaturalSize, { once: true });
    }

    // One photo means no prev, no next and no counter. A control that
    // cannot change anything is not drawn.
    var many = total > 1;
    if (prevBtn) prevBtn.hidden = !many;
    if (nextBtn) nextBtn.hidden = !many;
    if (counterEl) {
      counterEl.hidden = !many;
      counterEl.textContent =
        many && counterTemplate
          ? counterTemplate
              .replace("DIGXCURRENT", String(state.index + 1))
              .replace("DIGXTOTAL", String(total))
          : "";
    }
  }

  /**
   * Pins the page where it is. `overflow: hidden` alone loses the scroll
   * position on iOS Safari, so the body is fixed at a negative offset and
   * put back with an explicit scrollTo on close. The padding replaces the
   * scrollbar the lock removes, otherwise the whole page jumps ~15px wider
   * the instant a photo opens.
   */
  function lockScroll() {
    // Idempotent by contract -- see state.locked above.
    if (state.locked) return;
    state.locked = true;
    state.scrollY = window.pageYOffset || document.documentElement.scrollTop || 0;
    state.bodyTop = document.body.style.top;
    state.bodyPadRight = document.body.style.paddingRight;

    var gutter = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.top = -state.scrollY + "px";
    if (gutter > 0) document.body.style.paddingRight = gutter + "px";
    document.documentElement.classList.add("digx-lightbox-open");
  }

  function unlockScroll() {
    if (!state.locked) return;
    state.locked = false;
    document.documentElement.classList.remove("digx-lightbox-open");
    // Restore whatever was inline before, which is normally nothing --
    // never blindly blank a property the theme might own.
    document.body.style.top = state.bodyTop;
    document.body.style.paddingRight = state.bodyPadRight;

    // Read a layout property to force a SYNCHRONOUS reflow before restoring
    // the offset. While the body is `position: fixed` the document collapses
    // to viewport height, so the scroll maximum is far smaller than the saved
    // offset and scrollTo() silently CLAMPS to it. Measured 2026-08-28 on a
    // real product page: a clean open/close cycle landed at 1906 instead of
    // 2821, which reads as the page quietly losing the shopper's place.
    void document.body.offsetHeight;

    // `instant`, not the default: a theme-level `scroll-behavior: smooth`
    // would otherwise animate this and any wheel event mid-animation would
    // abandon the restore partway.
    try {
      window.scrollTo({ top: state.scrollY, left: 0, behavior: "instant" });
    } catch (e) {
      window.scrollTo(0, state.scrollY);
    }
  }

  function open(anchor) {
    var card = anchor.closest(".digx-review__card");
    if (!card) return false;

    var items = collectFrom(card);
    if (items.length === 0) return false;

    state.items = items;
    state.opener = anchor;
    fillInfo(card);

    // The clicked photo's own index, per the markup contract. The DOM
    // position is the fallback: it is derived from the same list the
    // overlay is about to page through, so it is right even if the
    // attribute is missing, non-numeric or out of range.
    var start = parseInt(anchor.getAttribute("data-media-index"), 10);
    if (isNaN(start) || start < 0 || start >= items.length) {
      start = 0;
      for (var i = 0; i < items.length; i += 1) {
        if (items[i].anchor === anchor) {
          start = i;
          break;
        }
      }
    }

    show(start);
    lockScroll();
    lightbox.hidden = false;
    dialog.focus();
    return true;
  }

  function close() {
    if (lightbox.hidden) return;

    lightbox.hidden = true;
    unlockScroll();
    stageImg.removeAttribute("src");
    stageImg.hidden = true;
    // The next open belongs to whatever photo it opens, not to this one.
    clearNaturalSize();

    var opener = state.opener;
    state.opener = null;
    state.items = [];

    // Focus goes back to the photo the shopper opened, so a keyboard user
    // carries on from where they were instead of at the top of the page.
    // preventScroll because unlockScroll has already put the page back
    // exactly where it was and focus() would otherwise be allowed to move
    // it again; the try/catch is for engines that do not accept the option.
    if (opener && typeof opener.focus === "function") {
      try {
        opener.focus({ preventScroll: true });
      } catch (e) {
        opener.focus();
      }
    }
  }

  /**
   * Tab and Shift+Tab stay inside the dialog. Matched on the elements' own
   * `hidden` attribute, so the prev/next buttons of a single-photo review
   * are not tab stops -- the app's own version learned that one the hard
   * way (its W2 review note).
   */
  function focusTrap(event) {
    var focusable = dialog.querySelectorAll(
      'button:not([hidden]), [href], [tabindex]:not([tabindex="-1"])'
    );
    if (focusable.length === 0) return;

    var first = focusable[0];
    var last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  document.addEventListener("click", function (event) {
    var target = event.target;
    if (!target || typeof target.closest !== "function") return;

    // Close, prev and next first, and only while the overlay is open: these
    // three attributes exist nowhere else, but ordering the checks this way
    // means a click inside the dialog can never be read as a photo click.
    if (!lightbox.hidden) {
      if (target.closest("[data-digx-lightbox-close]")) {
        event.preventDefault();
        close();
        return;
      }
      if (target.closest("[data-digx-lightbox-next]")) {
        event.preventDefault();
        show(state.index + 1);
        return;
      }
      if (target.closest("[data-digx-lightbox-prev]")) {
        event.preventDefault();
        show(state.index - 1);
        return;
      }
    }

    var anchor = target.closest("[data-digx-lightbox-open]");
    if (!anchor) return;

    // A modified click is a deliberate request for the LINK: open in a new
    // tab, save the image, open in a new window. The anchor is real, so
    // honour it and do not preventDefault.
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    // preventDefault only once the overlay has actually opened. If anything
    // above returned false the anchor still navigates to the full image,
    // which is the no-JS behaviour and is never worse than nothing
    // happening at all.
    if (open(anchor)) event.preventDefault();
  });

  document.addEventListener("keydown", function (event) {
    if (lightbox.hidden) return;

    // "Esc" is the old Edge/IE spelling, still worth two words.
    if (event.key === "Escape" || event.key === "Esc") {
      event.preventDefault();
      close();
      return;
    }

    if (event.key === "Tab") {
      focusTrap(event);
      return;
    }

    // The arrows only mean something when there is somewhere to go.
    if (state.items.length < 2) return;

    if (event.key === "ArrowRight") {
      event.preventDefault();
      show(state.index + 1);
    } else if (event.key === "ArrowLeft") {
      event.preventDefault();
      show(state.index - 1);
    }
  });
})();
